import RootState from '@/store/rootState';
import Barn from '@/core/interfaces/Barn';
import Fence from '@/core/interfaces/Fence';
import Farm from '@/core/interfaces/Farm';
import VehicleType from '@/core/interfaces/VehicleType';
import Vehicle from '@/core/interfaces/Vehicle';
import User from '@/core/interfaces/User';
import AvailableVehicleType from '@/core/interfaces/AvailableVehicleType';
import FeedType from '@/core/interfaces/FeedType';
import VehicleAction from '@/core/interfaces/VehicleAction';
import AvailableVehicleTypeAction from '@/core/interfaces/AvailableVehicleTypeAction';
import Application from '@/core/interfaces/Application';
import Locale from '@/core/interfaces/Locale';
import ArbiterZone from '@/core/interfaces/ArbiterZone';
import TimeZone from '@/core/interfaces/TimeZone';
import UserGroup from '@/core/interfaces/UserGroup';
import FarmGroup from '@/core/interfaces/FarmGroup';
import Role from '@/core/interfaces/Role';
import arrayUnique from 'array-unique';
import ZetaHubzone from '@/core/interfaces/ZetaHubzone';
import Module from '@/core/interfaces/Module';
import GroundType from '@/core/interfaces/GroundType';
import CropType from '@/core/interfaces/CropType';

const sortAZ = <T>(values: T[], key: keyof T): T[] => values
    .sort((a, b) => (String(a[key])).trim().localeCompare(String(b[key]).trim()));

export const farms = (state: RootState): Farm[] => sortAZ(state.farms, 'name');

export const farmOptions = (state: RootState, getters: any): { value: number, label: string }[] => getters
    .farms.map((farm: Farm) => ({
        value: farm.id,
        label: `${farm.name} (Code: ${farm.code_name}${farm.movex_number ? ` - Movex Number: ${farm.movex_number}` : ''})`,
    }));

export const users = (state: RootState): User[] => sortAZ(state.users, 'username');

export const vehicles = (state: RootState): Vehicle[] => sortAZ(state.vehicles, 'name');

export const vehicleTypes = (state: RootState): VehicleType[] => sortAZ(state.vehicleTypes, 'name');

export const timeZones = (state: RootState): TimeZone[] => sortAZ(state.timeZones, 'name');

export const barnsByFarm = (state: RootState) => (farmId: number): Barn[] => state
    .barns
    .filter(barn => barn.farm_id === farmId);

export const vehicleTypeById = (state: RootState) => (vehicleTypeId: number): VehicleType | null => state
    .vehicleTypes
    .find(vehicleType => vehicleType.id === vehicleTypeId) || null;

export const farmById = (state: RootState) => (vehicleTypeId: number): Farm | null => state
    .farms
    .find(farm => farm.id === vehicleTypeId) || null;

export const barnById = (state: RootState) => (vehicleTypeId: number): Barn | null => state
    .barns
    .find(barn => barn.id === vehicleTypeId) || null;

export const fenceById = (state: RootState) => (vehicleTypeId: number): Fence | null => state
    .fences
    .find(fence => fence.id === vehicleTypeId) || null;

export const userById = (state: RootState) => (userId: number): User | null => state
    .users
    .find(user => user.id === userId) || null;

export const fencesByBarn = (state: RootState) => (barnId: number): Fence[] => state
    .fences
    .filter(fence => fence.barn_id === barnId);

export const vehiclesByFarm = (state: RootState) => (farmId: number): Vehicle[] => state
    .vehicles
    .filter(vehicle => vehicle.farm_id === farmId);

export const vehiclesByFarmAndVehicleType = (state: RootState, getters: any) => (farmId: number, vehicleTypeId: number): Vehicle[] => (getters
    .vehiclesByFarm(farmId) as Vehicle[])
    .filter(vehicle => vehicle.vehicle_type_id === vehicleTypeId);

export const availableVehicleTypesByFarm = (state: RootState) => (farmId: number): AvailableVehicleType[] => state
    .availableVehicleTypes
    .filter(availableVehicleType => availableVehicleType.farm_id === farmId);

export const vehicleTypesByFarm = (state: RootState, getters: any) => (farmId: number): VehicleType[] => sortAZ(getters
    .availableVehicleTypesByFarm(farmId)
    .map((availableVehicleType: AvailableVehicleType) => vehicleTypeById(state)(availableVehicleType.vehicle_type_id) as VehicleType), 'name');

export const fencesByFarm = (state: RootState) => (farmId: number): Fence[] => barnsByFarm(state)(farmId)
    .flatMap(barn => fencesByBarn(state)(barn.id));

export const feedTypesByFarm = (state: RootState) => (farmId: number): FeedType[] => sortAZ(state
    .feedTypes
    .filter(feedType => feedType.farm_id === farmId), 'name');

export const vehicleActions = (state: RootState): VehicleAction[] => sortAZ(state
    .vehicleActions, 'name');

export const availableActionsByAvailableVehicleType = (state: RootState) => (availableVehicleType: number): AvailableVehicleTypeAction[] => state
    .availableVehicleTypeActions
    .filter(action => action.farm_available_vehicle_type_id === availableVehicleType);

export const vehicleActionById = (state: RootState) => (vehicleActionId: number): VehicleAction | null => state
    .vehicleActions
    .find(vehicleAction => vehicleAction.id === vehicleActionId) || null;

export const applications = (state: RootState): Application[] => sortAZ(state
    .applications, 'code');

export const applicationById = (state: RootState) => (applicationId: number): Application | null => state
    .applications
    .find(applications => applications.id === applicationId) || null;

export const locales = (state: RootState): Locale[] => sortAZ(state
    .locales, 'name');

export const localeById = (state: RootState) => (localeId: number): Locale | null => state
    .locales
    .find(locale => locale.id === localeId) || null;

export const arbiterZonesByFarm = (state: RootState) => (farmId: number): ArbiterZone[] => {
    const barnIdsInFarm = barnsByFarm(state)(farmId).map(barn => barn.id);

    return sortAZ(state.arbiterZones, 'name')
        .filter(arbiterZone => arbiterZone.farm_id === farmId || (arbiterZone.barn_id && barnIdsInFarm.includes(arbiterZone.barn_id)));
};

export const zetaHubZonesByFarm = (state: RootState) => (farmId: number): ZetaHubzone[] => sortAZ(state.zetaHubZones, 'name')
    .filter(zetaHubZone => zetaHubZone.farm_id === farmId);

export const userGroups = (state: RootState) => sortAZ(state.userGroups, 'name');

export const farmGroups = (state: RootState) => sortAZ(state.farmGroups, 'name');

export const roles = (state: RootState) => sortAZ(state.roles, 'name');

export const userGroupById = (state: RootState) => (userGroupId: number): UserGroup | null => state.userGroups
    .find(userGroup => userGroup.id === userGroupId) || null;

export const farmGroupById = (state: RootState) => (farmGroupId: number): FarmGroup | null => state.farmGroups
    .find(farmGroup => farmGroup.id === farmGroupId) || null;

export const roleById = (state: RootState) => (roleId: number): Role | null => state.roles
    .find(role => role.id === roleId) || null;

const resolveIds = <T>(ids: number[] | undefined, byId: (id: number) => T | null): T[] => (ids || [])
    .map(byId)
    .filter((item): item is T => !!item);

export const userGroupsByUser = (state: RootState) => (userId: number): UserGroup[] => sortAZ(state.userGroups
    .filter(userGroup => (userGroup.user_ids || []).includes(userId)), 'name');

export const rolesByUserGroup = (state: RootState, getters: any) => (userGroupId: number): Role[] => sortAZ(
    resolveIds<Role>((getters.userGroupById(userGroupId) as UserGroup | null)?.role_ids, getters.roleById),
    'name',
);

export const vehicleTypesByUserGroup = (state: RootState, getters: any) => (userGroupId: number): VehicleType[] => sortAZ(
    resolveIds<VehicleType>((getters.userGroupById(userGroupId) as UserGroup | null)?.vehicle_types_ids, getters.vehicleTypeById),
    'name',
);

export const farmGroupsByUserGroup = (state: RootState, getters: any) => (userGroupId: number): FarmGroup[] => sortAZ(
    resolveIds<FarmGroup>((getters.userGroupById(userGroupId) as UserGroup | null)?.farm_group_ids, getters.farmGroupById),
    'name',
);

export const farmGroupsByFarm = (state: RootState) => (farmId: number): FarmGroup[] => sortAZ(state.farmGroups
    .filter(farmGroup => (farmGroup.farm_ids || []).includes(farmId)), 'name');

export const farmsByFarmGroup = (state: RootState, getters: any) => (farmGroupId: number): Farm[] => sortAZ(
    resolveIds<Farm>((getters.farmGroupById(farmGroupId) as FarmGroup | null)?.farm_ids, getters.farmById),
    'name',
);

export const rolesByUser = (state: RootState, getters: any) => (userId: number): Role[] => sortAZ(arrayUnique(
    (getters
        .userGroupsByUser(userId) as UserGroup[])
        .flatMap(userGroup => getters.rolesByUserGroup(userGroup.id)),
), 'name');

export const usersByUserGroup = (state: RootState, getters: any) => (userGroupId: number): User[] => sortAZ(
    resolveIds<User>((getters.userGroupById(userGroupId) as UserGroup | null)?.user_ids, getters.userById),
    'username',
);

export const userGroupsByFarmGroup = (state: RootState) => (farmGroupId: number): UserGroup[] => sortAZ(state.userGroups
    .filter(userGroup => (userGroup.farm_group_ids || []).includes(farmGroupId)), 'name');

export const farmsByUserGroup = (state: RootState, getters: any) => (userGroupId: number): Farm[] => (getters
    .farmGroupsByUserGroup(userGroupId) as FarmGroup[])
    .flatMap(farmGroup => getters.farmsByFarmGroup(farmGroup.id));

export const usersByFarm = (state: RootState, getters: any) => (farmId: number): User[] => (
    getters.users as User[])
    .filter(user => user.farm_id === farmId);

export const modules = (state: RootState): Module[] => state.modules.sort((a, b) => a.name.localeCompare(b.name));

export const groundTypes = (state: RootState): GroundType[] => state.groundTypes.sort((a, b) => a.name.localeCompare(b.name));

export const groundTypeById = (state: RootState) => (groundTypeId: number): GroundType | null => state
    .groundTypes.find(groundType => groundType.id === groundTypeId) || null;

export const cropTypes = (state: RootState): CropType[] => state.cropTypes.sort((a, b) => a.name.localeCompare(b.name));

export const cropTypeById = (state: RootState) => (cropTypeId: number): CropType | null => state
    .cropTypes.find(cropType => cropType.id === cropTypeId) || null;

export const relayServers = (state: RootState) => state.relayServers.sort((a, b) => a.hostname.localeCompare(b.hostname));

export const relayServerById = (state: RootState) => (relayServerId: number) => state
    .relayServers.find(relayServer => relayServer.id === relayServerId) || null;
